import { useState, useEffect, useContext, useMemo } from 'react';
import { AppContext } from '../context/AppContext';
import { ROLE_META } from '../utils/constants';
import { loadArchivedRestaurants, archiveRestaurant } from '../services/restaurantService';

/**
 * Archive Page - Admin Only
 * Shows archived restaurants (deal_closed or manually archived)
 */
export default function ArchivePage() {
  const ctx = useContext(AppContext);
  const { currentUser, showToast } = ctx;
  
  const [archivedRestaurants, setArchivedRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [unarchiving, setUnarchiving] = useState(null);

  // Load archived restaurants
  useEffect(() => {
    const loadArchived = async () => {
      setLoading(true);
      try {
        const data = await loadArchivedRestaurants(currentUser);
        setArchivedRestaurants(data);
      } catch (err) {
        console.error('Load archived error:', err);
        showToast('❌ فشل في تحميل الأرشيف', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser?.role === 'admin') {
      loadArchived();
    }
  }, [currentUser]);

  // Filter archived restaurants
  const filteredRestaurants = useMemo(() => {
    let rs = archivedRestaurants || [];

    if (search) {
      rs = rs.filter(r => 
        r.name.toLowerCase().includes(search.toLowerCase()) ||
        r.ownerName?.toLowerCase().includes(search.toLowerCase()) ||
        r.area?.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      rs = rs.filter(r => r.status === statusFilter);
    }

    return rs;
  }, [archivedRestaurants, search, statusFilter]);

  // Unarchive restaurant
  const handleUnarchive = async (restaurant) => {
    if (!confirm(`هل تريد إرجاع "${restaurant.name}" إلى القائمة الرئيسية؟`)) {
      return;
    }

    setUnarchiving(restaurant.id);
    try {
      const success = await archiveRestaurant(restaurant.id, false);
      if (success) {
        showToast('✅ تم إرجاع المطعم بنجاح');
        setArchivedRestaurants(prev => prev.filter(r => r.id !== restaurant.id));
        if (ctx.loadRestaurants) {
          ctx.loadRestaurants();
        }
      }
    } catch (err) {
      console.error('Unarchive error:', err);
      showToast('❌ فشل في إرجاع المطعم', 'error');
    } finally {
      setUnarchiving(null);
    }
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h2 style={{ color: '#000000' }}>غير مصرح</h2>
        <p style={{ color: '#000000' }}>هذه الصفحة متاحة للمسؤول فقط</p>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ padding: 24 }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ color: '#000000', fontSize: 20, fontWeight: 900, marginBottom: 4 }}>
            📦 الأرشيف
          </h1>
          <p style={{ color: '#3d5470', fontSize: 13 }}>
            {filteredRestaurants.length} مطعم مؤرشف
          </p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ 
        background: '#ffffff',
        border: '1px solid #e2e8f0', 
        borderRadius: 12, 
        padding: 16, 
        marginBottom: 20,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <input
          type="text"
          placeholder="🔍 بحث بالاسم، المالك، المنطقة..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1,
            minWidth: 200,
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: '10px 14px',
            color: '#0f172a',
            fontSize: 14,
            fontFamily: "'Cairo',sans-serif",
          }}
        />
      
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: '10px 14px',
            color: '#0f172a',
            fontSize: 14,
            fontFamily: "'Cairo',sans-serif",
            cursor: 'pointer',
          }}
        >
          <option value="all">جميع الحالات</option>
          <option value="deal_closed">✅ صفقة مغلقة</option>
          <option value="archived">📦 غير مهتم</option>
        </select>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 60, color: '#000000' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
          <p>جاري تحميل الأرشيف...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredRestaurants.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: '#000000' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
          <p>لا يوجد مطاعم مؤرشفة</p>
        </div>
      )}

      {/* Archived Restaurants Table */}
      {!loading && filteredRestaurants.length > 0 && (
        <div style={{ 
          background: '#ffffff',
          border: '1px solid #e2e8f0', 
          borderRadius: 12,
          overflow: 'auto',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '14px 16px', textAlign: 'right', color: '#000000', fontSize: 12, fontWeight: 700 }}>المطعم</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', color: '#000000', fontSize: 12, fontWeight: 700 }}>الحالة</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', color: '#000000', fontSize: 12, fontWeight: 700 }}>المنطقة</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', color: '#000000', fontSize: 12, fontWeight: 700 }}>تاريخ الأرشفة</th>
                <th style={{ padding: '14px 16px', textAlign: 'center', color: '#000000', fontSize: 12, fontWeight: 700 }}>إجراء</th>
              </tr>
            </thead>
            <tbody>
              {filteredRestaurants.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ color: '#0f172a', fontWeight: 700 }}>{r.name}</div>
                    <div style={{ color: '#64748b', fontSize: 12 }}>{r.ownerName || '—'}</div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{
                      background: r.status === 'deal_closed' ? '#10b98122' : '#f59e0b22',
                      color: r.status === 'deal_closed' ? '#10b981' : '#f59e0b',
                      padding: '4px 10px',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 700,
                    }}>
                      {r.status === 'deal_closed' ? '✅ صفقة مغلقة' : '📦 غير مهتم'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', color: '#64748b', fontSize: 13 }}>
                    {r.area || '—'}
                  </td>
                  <td style={{ padding: '14px 16px', color: '#64748b', fontSize: 13 }}>
                    {r.updatedAt ? new Date(r.updatedAt).toLocaleDateString('ar-AE') : '—'}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleUnarchive(r)}
                      disabled={unarchiving === r.id}
                      style={{
                        background: unarchiving === r.id ? '#cbd5e1' : '#10b981',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        padding: '8px 16px',
                        cursor: unarchiving === r.id ? 'wait' : 'pointer',
                        fontSize: 12,
                        fontWeight: 700,
                        fontFamily: "'Cairo',sans-serif",
                      }}
                    >
                      {unarchiving === r.id ? '⏳ جاري...' : '🔄 إرجاع'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
